import {
  Dialog, DialogContent, DialogActions, Box, Typography, Button,
  IconButton, Tooltip, CircularProgress, Alert,
} from '@mui/material';
import {
  Print as PrintIcon, Description as WordIcon, TableChart as ExcelIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

const esc = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));

// ── Amount-to-words ────────────────────────────────────────────
const ONES = ['', 'One','Two','Three','Four','Five','Six','Seven','Eight','Nine',
              'Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
const TENS = ['', '','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
function under1000(n) {
  if (n === 0) return '';
  if (n < 20) return ONES[n];
  if (n < 100) return TENS[Math.floor(n/10)] + (n%10 ? ' ' + ONES[n%10] : '');
  return ONES[Math.floor(n/100)] + ' Hundred' + (n%100 ? ' and ' + under1000(n%100) : '');
}
function intToWords(n) {
  if (n === 0) return 'Zero';
  if (n < 0) return 'Negative ' + intToWords(-n);
  const parts = [];
  const million  = Math.floor(n/1_000_000); n %= 1_000_000;
  const thousand = Math.floor(n/1_000);     n %= 1_000;
  if (million)  parts.push(under1000(million)  + ' Million');
  if (thousand) parts.push(under1000(thousand) + ' Thousand');
  if (n)        parts.push(under1000(n));
  return parts.join(' ');
}
function amountToWords(amount) {
  const num = Math.abs(Number(amount) || 0);
  const cedis   = Math.floor(num);
  const pesewas = Math.round((num - cedis) * 100);
  let s = intToWords(cedis) + ' Ghana Cedi' + (cedis === 1 ? '' : 's');
  if (pesewas > 0) s += ' and ' + intToWords(pesewas) + ' Pesewa' + (pesewas === 1 ? '' : 's');
  return s + ' Only';
}

// ── Shared CSS — inline-styled HTML survives outerHTML serialization ──
const RECEIPT_CSS = `
  .receipt-sheet { position: relative; background: #fff; color: #000;
    font-family: 'Times New Roman', Georgia, serif; padding: 36px 48px;
    max-width: 820px; margin: 0 auto; overflow: hidden; box-sizing: border-box; }
  .receipt-watermark { position: absolute; top: 50%; left: 50%;
    transform: translate(-50%, -50%) rotate(-22deg); width: 70%;
    max-width: 520px; opacity: 0.06; pointer-events: none; z-index: 0; }
  .receipt-content { position: relative; z-index: 1; }
  .receipt-header { display: table; width: 100%; margin-bottom: 8px; }
  .receipt-header > div { display: table-cell; vertical-align: middle; }
  .receipt-logo { width: 70px; height: 70px; object-fit: contain; display: block; }
  .receipt-school-name { font-weight: 800; font-size: 22px;
    text-decoration: underline; text-transform: uppercase; letter-spacing: 0.5px; text-align: center; }
  .receipt-info { width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 12px; }
  .receipt-info td { padding: 6px 8px; vertical-align: top; }
  .receipt-info .lbl { font-weight: 700; white-space: nowrap; }
  .receipt-divider { border: none; border-top: 1px solid #000; margin: 12px 0; }
  .receipt-title { text-align: center; font-weight: 800; font-size: 18px;
    text-decoration: underline; margin: 6px 0 14px 0; }
  .receipt-date { font-size: 14px; margin-bottom: 12px; }
  .receipt-box { border: 1px solid #000; padding: 14px; margin-bottom: 14px; font-size: 13px; }
  .receipt-section-h { font-weight: 800; font-size: 13px; margin-bottom: 8px; }
  .receipt-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 14px; }
  .receipt-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 6px 14px; }
  .receipt-amount-box { padding: 10px; border: 1px solid #000; font-weight: 800;
    background: #fafafa; text-align: right; font-size: 16px; margin-bottom: 8px; }
  .receipt-in-words { margin: 10px 0; padding: 8px 12px; border: 1px solid #000;
    background: #fafafa; font-style: italic; font-size: 13px; }
  .receipt-in-words b { font-style: normal; font-weight: 700; }
  .receipt-table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
  .receipt-table th { border: 1px solid #000; padding: 6px 8px; background: #f0f0f0;
    text-align: left; font-weight: 700; }
  .receipt-table td { border: 1px solid #000; padding: 6px 8px; }
  .receipt-table .right { text-align: right; }
  .receipt-history-h { text-align: center; font-weight: 800; font-size: 14px; margin: 4px 0 6px 0; }
  .receipt-signature { margin-top: 50px; padding-top: 4px; }
  .receipt-signature .line { width: 280px; border-top: 1px dotted #000; padding-top: 4px; font-weight: 700; }
  @media print { body { background: #fff; margin: 0; }
    .receipt-sheet { padding: 24px 30px; max-width: 100%; }
    .receipt-watermark { opacity: 0.08; } }
`;

function headerHTML(data) {
  const s = data.school || {};
  const pay = data.payment || {};
  const logo = s.logo
    ? `<img class="receipt-logo" src="${esc(s.logo)}" alt="logo" />`
    : '';
  return `
    <div class="receipt-header">
      <div style="width:70px;">${logo}</div>
      <div><div class="receipt-school-name">${esc(s.school_name || 'School Name')}</div></div>
      <div style="text-align:right;width:70px;"></div>
    </div>
    <table class="receipt-info">
      <tr>
        <td class="lbl">Address:</td><td>${esc(s.address || '—')}</td>
        <td class="lbl" style="text-align:right;">Tel#:</td>
        <td style="text-align:right;">${esc(s.phone || '—')}</td>
      </tr>
      <tr>
        <td class="lbl">Location:</td><td>${esc(s.region || '—')}</td>
        <td class="lbl" style="text-align:right;">Email:</td>
        <td style="text-align:right;">${esc(s.email || '—')}</td>
      </tr>
      <tr>
        <td class="lbl">Mission:</td><td>${esc(s.motto || '—')}</td>
        <td class="lbl" style="text-align:right;">Receipt:</td>
        <td style="text-align:right;font-weight:700;">${esc(pay.receipt_no || '—')}</td>
      </tr>
    </table>
    <hr class="receipt-divider" />
    <div class="receipt-date"><b>Date:</b> ${esc(fmtDate(pay.pay_date))}</div>
  `;
}

function payerInfoHTML(pay) {
  const isStudent = !!(pay.student_id && pay.student_id !== '-');
  return `
    <div class="receipt-box">
      <div class="receipt-section-h">${isStudent ? 'STUDENT' : 'PAYER'} INFORMATION</div>
      <div class="receipt-grid-3">
        ${isStudent
          ? `<div><b>Index:</b> ${esc(pay.student_id)}</div>
             <div><b>Name:</b> ${esc(pay.payer_name || '—')}</div>
             <div><b>Class:</b> ${esc(pay.class_name || '—')}</div>`
          : `<div><b>Payer:</b> ${esc(pay.payer_name || '—')}</div>
             <div><b>Class:</b> ${esc(pay.class_name || '—')}</div>
             <div><b>Type:</b> ${esc(pay.payment_type || '—')}</div>`
        }
        <div><b>Term:</b> ${esc(pay.term || '—')}</div>
        <div><b>Academic Year:</b> ${esc(pay.acc_yer || '—')}</div>
        <div><b>Mode:</b> ${esc(pay.mode || '—')}</div>
      </div>
    </div>
  `;
}

function bodyHTML(data) {
  const pay = data.payment || {};
  const allocations = data.allocations || [];
  const items = data.items || [];
  const amount = Number(pay.total_amount || 0);

  // What was bought. A receipt covering several things — a uniform and
  // exercise books paid together — stores 'MULTIPLE' in payment_type,
  // so without this table the buyer would get a receipt that names
  // nothing they actually paid for. Skipped for a single item, where
  // the title already says it.
  let itemTable = '';
  if (items.length > 1) {
    itemTable = `
      <div class="receipt-history-h">Items paid for</div>
      <table class="receipt-table">
        <thead><tr>
          <th>#</th><th>Item</th><th class="right">Amount</th>
        </tr></thead>
        <tbody>
          ${items.map((it, i) => `
            <tr>
              <td>${i + 1}</td>
              <td>${esc(it.income_type || '—')}</td>
              <td class="right">GHS ${fmtMoney(it.amount)}</td>
            </tr>`).join('')}
          <tr style="background:#f5f5f5;font-weight:800;">
            <td colspan="2" class="right">Total</td>
            <td class="right">GHS ${fmtMoney(
              items.reduce((s, it) => s + Number(it.amount || 0), 0)
            )}</td>
          </tr>
        </tbody>
      </table>
    `;
  }

  let allocTable = '';
  if (allocations.length > 0) {
    const totalAlloc = allocations.reduce((s, a) => s + Number(a.amount_paid || 0), 0);
    allocTable = `
      <div class="receipt-history-h">Allocation</div>
      <table class="receipt-table">
        <thead><tr>
          <th>#</th><th>Index</th><th>Name</th><th>Class</th>
          <th class="right">Amount</th><th>Remarks</th>
        </tr></thead>
        <tbody>
          ${allocations.map((a, i) => `
            <tr>
              <td>${i + 1}</td>
              <td>${esc(a.student_id || '—')}</td>
              <td>${esc(a.student_name || '—')}</td>
              <td>${esc(a.class_name || '—')}</td>
              <td class="right">GHS ${fmtMoney(a.amount_paid)}</td>
              <td>${esc(a.remarks || '')}</td>
            </tr>`).join('')}
          <tr style="background:#f5f5f5;font-weight:800;">
            <td colspan="4" class="right">Total Allocated</td>
            <td class="right">GHS ${fmtMoney(totalAlloc)}</td>
            <td></td>
          </tr>
        </tbody>
      </table>
    `;
  }

  // 'MULTIPLE' is a database label, not something to print on a receipt.
  const title = items.length > 1
    ? 'Payment'
    : (items[0]?.income_type || pay.payment_type || 'Payment');

  // The info block prints a "Type:" line straight from payment_type.
  // Show the real income types there instead of the 'MULTIPLE' marker.
  const payForInfo = {
    ...pay,
    payment_type: items.length
      ? items.map(it => it.income_type).join(', ')
      : pay.payment_type,
  };

  return `
    <div class="receipt-title">${esc(title)} Receipt</div>
    ${headerHTML(data)}
    ${payerInfoHTML(payForInfo)}
    ${itemTable}
    <div class="receipt-amount-box">
      Amount Paid: GHS ${fmtMoney(amount)}
    </div>
    <div class="receipt-in-words">
      <b>In words:</b> ${esc(amountToWords(amount))}
    </div>
    ${pay.reference_no ? `<div style="font-size:13px;margin-bottom:10px;"><b>Reference:</b> ${esc(pay.reference_no)}</div>` : ''}
    ${pay.notes ? `<div style="font-size:13px;margin-bottom:10px;"><b>Notes:</b> ${esc(pay.notes)}</div>` : ''}
    <div style="font-size:13px;margin-bottom:10px;"><b>Received By:</b> ${esc(pay.received_by_name || '—')}</div>
    ${allocTable}
    ${pay.status === 'REVERSED'
      ? '<div style="margin-top:14px;padding:8px;border:2px solid #c62828;color:#c62828;font-weight:800;text-align:center;">*** REVERSED ***</div>'
      : ''}
    <div class="receipt-signature">
      <div class="line">Accountant Signature</div>
    </div>
  `;
}

function sheetHTML(data) {
  const watermark = data.school?.logo
    ? `<img class="receipt-watermark" src="${esc(data.school.logo)}" alt="" />`
    : '';
  return `<div class="receipt-sheet">${watermark}<div class="receipt-content">${bodyHTML(data)}</div></div>`;
}

function fullDocument(innerHTML, title) {
  return `<!DOCTYPE html><html><head>
    <meta charset="utf-8" />
    <title>${esc(title)}</title>
    <style>${RECEIPT_CSS}</style>
  </head><body>${innerHTML}</body></html>`;
}

function downloadBlob(content, mime, filename) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function OtherReceiptDialog({ open, onClose, data, loading, error }) {
  const receiptNo = data?.payment?.receipt_no || 'receipt';
  const html = data ? sheetHTML(data) : '';

  const handlePrint = () => {
    if (!data) return;
    const w = window.open('', '_blank', 'width=900,height=1000');
    if (!w) return;
    w.document.write(fullDocument(html, `Receipt ${receiptNo}`));
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 350);
  };

  const handleExportWord = () => {
    if (!data) return;
    downloadBlob(fullDocument(html, `Receipt ${receiptNo}`),
      'application/msword', `Receipt-${receiptNo}.doc`);
  };

  const handleExportExcel = () => {
    if (!data) return;
    const pay = data.payment || {};
    const allocs = data.allocations || [];
    const items = data.items || [];
    const rows = [];
    rows.push(['Receipt', pay.receipt_no || '']);
    rows.push(['Date', pay.pay_date || '']);
    rows.push(['Type', items.length
      ? items.map(it => it.income_type).join(', ')
      : (pay.payment_type || '')]);
    rows.push(['Payer', pay.payer_name || '']);
    rows.push(['Class', pay.class_name || '']);
    rows.push(['Mode', pay.mode || '']);
    rows.push(['Amount', pay.total_amount || 0]);
    rows.push(['Status', pay.status || '']);
    rows.push([]);
    if (items.length > 1) {
      rows.push(['Item', 'Amount']);
      items.forEach(it => rows.push([it.income_type || '', it.amount || 0]));
      rows.push([]);
    }
    if (allocs.length) {
      rows.push(['#', 'Index', 'Name', 'Class', 'Amount', 'Remarks']);
      allocs.forEach((a, i) => {
        rows.push([i + 1, a.student_id || '', a.student_name || '',
                   a.class_name || '', a.amount_paid || 0, a.remarks || '']);
      });
    }
    const escCsv = (v) => {
      if (v == null) return '';
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = rows.map(r => r.map(escCsv).join(',')).join('\n');
    downloadBlob(csv, 'text/csv;charset=utf-8', `Receipt-${receiptNo}.csv`);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent sx={{ p: 0, bgcolor: '#eef0f4' }}>
        {loading ? (
          <Box sx={{ p: 8, textAlign: 'center' }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }} color="text.secondary">Loading receipt…</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ p: 4 }}><Alert severity="error">{error}</Alert></Box>
        ) : data ? (
          <Box sx={{ py: 3, maxWidth: 880, mx: 'auto', px: { xs: 1, sm: 2 } }}>
            <Box sx={{ bgcolor: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              borderRadius: 1, overflow: 'hidden' }}>
              <style>{RECEIPT_CSS}</style>
              <div dangerouslySetInnerHTML={{ __html: html }} />
            </Box>
          </Box>
        ) : (
          <Box sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
            <Typography>Could not load receipt.</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5, bgcolor: '#fff', borderTop: '1px solid', borderColor: 'divider' }}>
        <Tooltip title="Close"><IconButton onClick={onClose}><CloseIcon /></IconButton></Tooltip>
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleExportExcel} startIcon={<ExcelIcon />}
          variant="outlined" disabled={!data} sx={{ textTransform: 'none', fontWeight: 700 }}>Excel</Button>
        <Button onClick={handleExportWord} startIcon={<WordIcon />}
          variant="outlined" disabled={!data} sx={{ textTransform: 'none', fontWeight: 700 }}>Word</Button>
        <Button onClick={handlePrint} startIcon={<PrintIcon />}
          variant="contained" disabled={!data} sx={{ textTransform: 'none', fontWeight: 800, px: 3 }}>Print</Button>
      </DialogActions>
    </Dialog>
  );
}
